import type { BarDatum } from "@nivo/bar"
import type { ComputedCell } from "@nivo/heatmap"
import type { Serie } from "@nivo/line"
import type { ScatterPlotRawSerie } from "@nivo/scatterplot"
import { isNumber } from "lodash"

import type { HeatMapData } from "../../components/graphs/HeatMap/ConfusionMatrix"
import type { SankeyChartProps } from "../../components/graphs/SankeyChart"
import { getTheme } from "../../hooks/UseTheme"
import type { ClassificationSetting } from "../../types/generated/api/ClassificationSetting"
import type {
  CSVToJsonReturnType,
  CurrentThresholdMetricsData,
  Metrics,
  ModelType,
  ThresholdData,
  ThresholdMetrics,
} from "./Interfaces"

const theme = getTheme()

// This function is used to parse the CSV string returned from the presigned URL to an Object
// with threshold values as keys and the threshold data as inner objects
export const CSVToJson = (csv: string): CSVToJsonReturnType => {
  // split at line breaks to construct an array of rows
  const lines = csv.split("\n")

  // split the first line at , to construct an array of CSV headers or obj keys
  const keys: string[] = lines[0]?.split(",") ?? []

  // this regex is for checking for the V1 CSV format
  const checkV1CsvRegex = /\bthreshold\b/

  if (checkV1CsvRegex.test(csv)) {
    // if the CSV is the V1 format split the rows array at index 1 because index 0 is the header line
    // and loop on it to construct the obj
    const data = lines.slice(1).reduce((acc, line) => {
      // if the line is empty then we skip it
      if (!line) return { ...acc }

      // split each line at , to get an array of values
      const values = line.split(",")

      // construct the inner data obj by looping on the keys after excluding the first key because it is the threshold key
      //which is used as a key for the outer Obj then populate the empty valueObj with data key and the corresponding value
      // the modulus operator here to map each piece of data to the corresponding key according to its index
      const value: Record<PropertyKey, string> = {}

      keys.slice(1).forEach((key, i, arr) => {
        const currentValue = values[i % arr.length]

        if (currentValue) value[key] = currentValue
      })

      // Construct the outer obj with the threshold values as keys and the inner data objects as value
      if (!values[0]) return { ...acc }

      const current = { [Math.round(+values[0])]: value }

      // return the final object
      return { ...current, ...acc }
    }, {})

    return { type: "V1", thresholdMetrics: data }
  }

  // if the CSV is the V2 format split the rows array at index 1 because index 0 is the header line
  // and loop on it to construct the obj
  const data = lines.slice(1).reduce((acc, line) => {
    // if the line is empty then we skip it
    if (!line) return { ...acc }

    // split each line at , to get an array of values
    const values = line.split(",")

    // construct the inner data obj by looping on the keys after excluding the first key because it is the threshold key
    //which is used as a key for the outer Obj then populate the empty valueObj with data key and the corresponding value
    // the modulus operator here to map each piece of data to the corresponding key according to its index
    const value: Record<PropertyKey, string> = {}
    keys.forEach((key, i, arr) => {
      const currentValue = values[i % arr.length]
      if (currentValue) value[key] = currentValue
    })

    if (!values[0] || !values[1]) return { ...acc }

    // Construct the outer obj with the `{neg_threshold}_{pos_threshold}` as keys and the inner data objects as value
    const current = { [`${Math.round(+values[0])}_${Math.round(+values[1])}`]: value }

    // return the final object
    return { ...current, ...acc }
  }, {})

  return { type: "V2", thresholdMetrics: data }
}

/**
 * Finds the current threshold metrics data based on given threshold values.
 * it the single slider value is provided then the threshold data is in V1 format else use the V2 format
 * with 2 sliders negative and positive
 * @param {Object} params - The parameters object.
 * @param {ThresholdMetrics} params.thresholdMetrics - The object containing threshold metrics data.
 * @param {number} [params.positiveValue] - The positive threshold value.
 * @param {number} [params.negativeValue] - The negative threshold value.
 * @param {number} [params.singleSliderValue] - The single slider threshold value.
 * @returns {ThresholdData | undefined} - The corresponding threshold data if found, otherwise undefined.
 */
export const currentThresholdMetricsData = (params: CurrentThresholdMetricsData): ThresholdData | undefined => {
  const { thresholdMetrics, negativeValue, positiveValue, singleSliderValue } = params

  if (singleSliderValue !== undefined) {
    return thresholdMetrics[singleSliderValue]
  }

  if (negativeValue && positiveValue) return thresholdMetrics[`${negativeValue}_${positiveValue}`]
}

/**
 * Checks if the confusion metrics values are valid by checking if the following keys are available and have valid numbers:
 * - `false_${positiveLabel}`
 * - `false_${negativeLabel}`
 * - `true_${positiveLabel}`
 * - `true_${negativeLabel}`
 * @param {ThresholdData} currentThresholdMetrics - The object containing confusion metrics data.
 * @param {string} positiveLabel - The positive label.
 * @param {string} negativeLabel - The negative label.
 * @returns {boolean} - true if the values are valid, otherwise false.
 */

const isConfusionMetricsValuesValid = (
  currentThresholdMetrics: ThresholdData,
  positiveLabel: string,
  negativeLabel: string,
): boolean =>
  isNumber(Number(currentThresholdMetrics[`false_${positiveLabel}`])) &&
  isNumber(Number(currentThresholdMetrics[`false_${negativeLabel}`])) &&
  isNumber(Number(currentThresholdMetrics[`true_${positiveLabel}`])) &&
  isNumber(Number(currentThresholdMetrics[`true_${negativeLabel}`]))

/**
 * Given the current threshold metrics data, the positive label, and negative label,
 * creates and returns a ConfusionMatrixInput[] that can be used to render a confusion matrix.
 *
 * The returned array will contain two objects (one for positive and one for negative),
 * each containing an id and an array of 2 objects with x and y properties.
 * The x property will be the label ("Positive" or "Negative"), and the y property will be the number of true or false positives/negatives.
 *
 * If the currentThresholdMetricsData is undefined, or if the values for
 * `false_${positiveLabel}`, `false_${negativeLabel}`, `true_${positiveLabel}`, or `true_${negativeLabel}`
 * are not valid numbers, an empty array will be returned.
 */
export const getConfusionMatrixData = (
  modelType: ModelType | undefined,
  currentThresholdMetricsData: ThresholdData | undefined,
  positiveLabel: string | undefined | null,
  negativeLabel: string | undefined | null,
  labels: string[],
  t: (key: string) => string,
): HeatMapData[] => {
  let rows: HeatMapData[] = []
  if (!modelType || !positiveLabel || !negativeLabel) return rows

  if (
    currentThresholdMetricsData &&
    isConfusionMetricsValuesValid(currentThresholdMetricsData, positiveLabel, negativeLabel)
  ) {
    rows = [
      {
        id: `${t("metrics.chartLabels.actual")} ${labels[1]}`,
        data: [
          {
            x: `${t("metrics.chartLabels.predicted")} ${labels[1]}`,
            y: Number(currentThresholdMetricsData[`true_${negativeLabel}`]),
          },
          {
            x: `${t("metrics.chartLabels.predicted")} ${labels[0]}`,
            y: Number(currentThresholdMetricsData[`false_${positiveLabel}`]),
          },
        ],
      },
      {
        id: `${t("metrics.chartLabels.actual")} ${labels[0]}`,
        data: [
          {
            x: `${t("metrics.chartLabels.predicted")} ${labels[1]}`,
            y: Number(currentThresholdMetricsData[`false_${negativeLabel}`]),
          },
          {
            x: `${t("metrics.chartLabels.predicted")} ${labels[0]}`,
            y: Number(currentThresholdMetricsData[`true_${positiveLabel}`]),
          },
        ],
      },
    ]
  }

  return rows
}

/**
 * Converts the given threshold metric data into a Sankey chart data format.
 *
 * The function takes in the current threshold metric data, positive label, and negative label as parameters.
 * It returns an object with a nodes and links property, which is suitable for passing into the SankeyChart component.
 *
 * The nodes property is an array of objects with the following properties:
 * - id: The id of the node, which is a string.
 * - uuid: The uuid of the node, which is a string.
 * - percentage: The percentage value of the node, which is a number.
 * - nodeType: The type of the node, which is either "label" or "node".
 * - name: The name of the node, which is a string.
 *
 * The links property is an array of objects with the following properties:
 * - source: The id of the source node, which is a string.
 * - target: The id of the target node, which is a string.
 * - value: The value of the link, which is a number.
 * - percentage: The percentage value of the link, which is a number.
 *
 * The function handles the case where the threshold metric data is undefined by returning an empty object.
 *
 * @param {ThresholdData | undefined} currentThresholdMetricData - The current threshold metric data.
 * @param {string} positiveLabel - The positive label.
 * @param {string} negativeLabel - The negative label.
 * @returns {SankeyChartProps["data"]} - The Sankey chart data.
 */
export const getSankeyChartData = (
  currentThresholdMetricData: ThresholdData | undefined,
  positiveLabel?: string,
  negativeLabel?: string,
): SankeyChartProps["data"] => {
  if (!currentThresholdMetricData) return { nodes: [], links: [] }

  const predictedPos_actualPos = Number(currentThresholdMetricData[`true_${positiveLabel}`])
  const predictedPos_actualNeg = Number(currentThresholdMetricData[`false_${positiveLabel}`])
  const predictedNeg_actualNeg = Number(currentThresholdMetricData[`true_${negativeLabel}`])
  const predictedNeg_actualPos = Number(currentThresholdMetricData[`false_${negativeLabel}`])

  const totalValue = predictedPos_actualPos + predictedPos_actualNeg + predictedNeg_actualNeg + predictedNeg_actualPos

  return {
    nodes: [
      {
        id: `predicted-${positiveLabel}`,
        uuid: `uuid-${positiveLabel}`,
        percentage: ((predictedPos_actualPos + predictedPos_actualNeg) / totalValue) * 100,
        nodeType: "label",
        name: `predicted-${positiveLabel}`,
        color: theme.palette.green.background[1],
      },
      {
        id: `actual-${positiveLabel}`,
        uuid: `uuid-${positiveLabel}`,
        percentage: ((predictedPos_actualPos + predictedNeg_actualPos) / totalValue) * 100,
        nodeType: "label",
        name: `actual-${positiveLabel}`,
        color: theme.palette.blue.background[1],
      },
      {
        id: `predicted-${negativeLabel}`,
        uuid: `uuid-${negativeLabel}`,
        percentage: ((predictedNeg_actualNeg + predictedNeg_actualPos) / totalValue) * 100,
        nodeType: "label",
        name: `predicted-${negativeLabel}`,
        color: theme.palette.pink.background[1],
      },
      {
        id: `actual-${negativeLabel}`,
        uuid: `uuid-${negativeLabel}`,
        percentage: ((predictedNeg_actualNeg + predictedPos_actualNeg) / totalValue) * 100,
        nodeType: "label",
        name: `actual-${negativeLabel}`,
        color: theme.palette.orange.background[1],
      },
    ],
    links: [
      {
        source: `predicted-${positiveLabel}`,
        target: `actual-${positiveLabel}`,
        value: predictedPos_actualPos,
        percentage: (predictedPos_actualPos / totalValue) * 100,
      },
      {
        source: `predicted-${positiveLabel}`,
        target: `actual-${negativeLabel}`,
        value: predictedPos_actualNeg,
        percentage: (predictedPos_actualNeg / totalValue) * 100,
      },
      {
        source: `predicted-${negativeLabel}`,
        target: `actual-${positiveLabel}`,
        value: predictedNeg_actualPos,
        percentage: (predictedNeg_actualPos / totalValue) * 100,
      },
      {
        source: `predicted-${negativeLabel}`,
        target: `actual-${negativeLabel}`,
        value: predictedNeg_actualNeg,
        percentage: (predictedNeg_actualNeg / totalValue) * 100,
      },
    ],
  }
}

/**
 * Takes metricsData and returns a formatted array of objects with label and value
 * properties for use in a bar chart. If metricsData is undefined or does not have
 * a feature_importance property, returns an empty array.
 */
export const getImportanceChartData = (metricsData: Metrics | undefined): BarDatum[] => {
  if (!metricsData || !metricsData?.feature_importance) return []

  return metricsData.feature_importance
    .map((feat) => ({
      label: feat.feature_name,
      value: feat.feature_importance,
    }))
    .sort((a, b) => b.value - a.value)
}

/**
 * Takes metricsData and returns a formatted array of objects with id, color, and data
 * properties for use in a line chart. If metricsData is undefined or does not have
 * a NPL_vs_AR property or if NPL_vs_AR does not have NPL and AR properties, returns
 * an empty array.
 *
 * @param {string } currentPositiveLabel - the current chosen positive Label.
 * @param {Metrics | undefined} metricsData - The object containing chart data.
 * @returns {Serie[]} - An array of objects with id, color, and data properties.
 */
export const getLineChartData = (
  t: (key: string) => string,
  currentPositiveLabel?: string,
  metricsData?: Metrics,
): Serie[] => {
  if (!metricsData?.NPL_vs_AR?.NPL || !metricsData?.NPL_vs_AR?.AR) return []

  const positiveLabel = metricsData?.target_col_mapping?.pos_label

  let formattedData: {
    x: number
    y: number
  }[] = []

  //handle reversing the line chart when the labels change
  if (positiveLabel === currentPositiveLabel) {
    formattedData = metricsData.NPL_vs_AR.NPL.map((val, i) => ({ x: metricsData.NPL_vs_AR.AR[i] ?? 0, y: val }))
  } else {
    formattedData = metricsData.NPL_vs_AR_reversed.NPL_reversed.map((val, i) => ({
      x: metricsData.NPL_vs_AR_reversed.AR_reversed[i] ?? 0,
      y: val,
    }))
  }

  return [
    {
      id: t("metrics.chartLabels.approvalRate"),
      color: theme.palette.blue.background[1],
      data: formattedData,
    },
  ]
}

/**
 * Generates distribution chart data for a line chart from the given metrics data.
 *
 * The function extracts positive and negative distribution data from the `density_plot`
 * property of the provided `metricsData`. It formats this data into an array of objects
 * each containing an `id`, `color`, and `data` properties suitable for rendering
 * with a line chart.
 *
 * If the `metricsData` is undefined or lacks necessary properties for positive and
 * negative distributions, an empty array is returned.
 *
 * @param {Metrics } metricsData - The object containing chart data.
 * @returns {Serie[]} - An array of objects with id, color, and data properties.
 */
export const getDistributionChartData = (
  t: (key: string) => string,
  positiveLabel?: string,
  negativeLabel?: string,
  metricsData?: Metrics,
): Serie[] => {
  if (
    !metricsData?.density_plot?.pos_distribution?.x ||
    !metricsData?.density_plot?.pos_distribution?.y ||
    !metricsData?.density_plot?.neg_distribution?.x ||
    !metricsData?.density_plot?.neg_distribution?.y
  )
    return []

  const posFormattedData = metricsData.density_plot.pos_distribution.x.map((val, i) => ({
    x: val,
    y: metricsData.density_plot.pos_distribution.y[i],
  }))
  const negFormattedData = metricsData.density_plot.neg_distribution.x.map((val, i) => ({
    x: val,
    y: metricsData.density_plot.neg_distribution.y[i],
  }))

  return [
    {
      id: `${t("metrics.chartLabels.positiveLabel")} (${positiveLabel})`,
      color: theme.palette.blue.text[2],
      data: posFormattedData,
    },
    {
      id: `${t("metrics.chartLabels.negativeLabel")} (${negativeLabel})`,
      color: theme.palette.red.text[2],
      data: negFormattedData,
    },
  ]
}

/**
 * Retrieves correlation funnel data from the provided chart data.
 *
 * @param {string } currentPositiveLabel - the current chosen positive Label.
 * @param {Metrics } metricsData - The object containing chart data.
 * @returns {ScatterPlotRawSerie<{ x: number; y: string; bin: string; decision?: string; reason?: string }>[]}
 */
export const getCorrelationFunnelData = (
  currentPositiveLabel?: string,
  metricsData?: Metrics,
): ScatterPlotRawSerie<{
  x: number
  y: string
  bin: string
  decision?: string
  reason?: string
}>[] => {
  if (!metricsData) return []

  const correlationFunnelData = metricsData.correlation_funnel
  const targetColumn = metricsData?.target_col_mapping?.target_column
  const positiveLabel = metricsData?.target_col_mapping?.pos_label
  const negativeLabel = metricsData?.target_col_mapping?.neg_label

  if (!targetColumn || !positiveLabel || !negativeLabel) return []

  const chartData = Object.entries(
    correlationFunnelData[`${targetColumn}______${positiveLabel}`]?.[`${targetColumn}______${negativeLabel}`] ?? {},
  ).map(([yAxisValues, bins]) => {
    return {
      id: yAxisValues,
      data: Object.entries(bins).map(([bin, value]) => {
        const separatedBinValues = bin.split("__")
        let formattedBin = bin
        if (
          separatedBinValues.length === 2 &&
          isNumber(Number(separatedBinValues[0])) &&
          isNumber(Number(separatedBinValues[1]))
        ) {
          formattedBin = `${separatedBinValues[0]} -> ${separatedBinValues[1]}`
        }

        return {
          x: value ? (positiveLabel === currentPositiveLabel ? value : value * -1) : 0,
          y: yAxisValues,
          bin: formattedBin,
        }
      }),
    }
  })

  // Sort the overall data array by the maximum absolute correlation value
  return chartData.sort((a, b) => {
    const maxAbsA = Math.max(...a.data.map((d) => Math.abs(d.x)))
    const maxAbsB = Math.max(...b.data.map((d) => Math.abs(d.x)))
    return maxAbsB - maxAbsA
  })
}

/**
 * Extracts the positive and negative configuration items from the given history data.
 * The logic to extract the items is based on the V2 flow where the positive item starts from 100
 * and the negative item starts from 0. The function also returns the current metrics data
 * by calling findCurrThresholdMetricsData with the lower bound of the positive item and the upper
 * bound of the negative item.
 *
 * @param {ClassificationSetting[] } currentHistoryData - The history data.
 * @param {ThresholdMetrics} thresholdMetrics - The object containing threshold metrics data.
 * @returns {{ posItem: ClassificationSetting , negItem: ClassificationSetting , currentMetricsData: ThresholdData  }}
 */
export const getAutoMLConfigHistoryData = (
  thresholdMetrics: ThresholdMetrics,
  type: ModelType,
  currentHistoryData?: ClassificationSetting[],
): {
  posItem?: ClassificationSetting
  negItem?: ClassificationSetting
  currentMetricsData?: ThresholdData
} => {
  // Positive configurations item and negative configurations item are reversed between V1 and V2 flow
  // because in the V1 flow the positive item starts from 0 but in the V2 flow the positive item starts from 100
  let currentMetricsData, posItem, negItem

  const labelsItems = currentHistoryData
    ?.filter((item) => item.type === "label")
    .sort((a, b) => a.lower_bound - b.upper_bound)

  if (!labelsItems || !labelsItems[0] || !labelsItems[1]) return { posItem, negItem, currentMetricsData }

  if (
    labelsItems[0].upper_bound < labelsItems[1].upper_bound ||
    (labelsItems[0].upper_bound === labelsItems[1].upper_bound &&
      labelsItems[0].lower_bound < labelsItems[1].lower_bound)
  ) {
    negItem = labelsItems[0]
    posItem = labelsItems[1]
  } else {
    posItem = labelsItems[0]
    negItem = labelsItems[1]
  }

  if (type === "V2") {
    currentMetricsData = currentThresholdMetricsData({
      thresholdMetrics: thresholdMetrics,
      positiveValue: posItem?.lower_bound,
      negativeValue: negItem?.upper_bound,
    })
  } else {
    currentMetricsData = currentThresholdMetricsData({
      thresholdMetrics: thresholdMetrics,
      singleSliderValue: negItem?.upper_bound,
    })
  }

  return { posItem, negItem, currentMetricsData }
}

/**
 * A function to format the cell data of the confusion matrix.
 * The function takes in a cell object and returns a string.
 * The string is formatted based on the cell id.
 * If the cell id is "Actual Positive.Predicted Positive", the string is "X True positive".
 * If the cell id is "Actual Negative.Predicted Positive", the string is "X False positive".
 * If the cell id is "Actual Positive.Predicted Negative", the string is "X False negative".
 * If the cell id is "Actual Negative.Predicted Negative", the string is "X True negative".
 * Otherwise, the string is the cell data y value as a string, or an empty string if it is null.
 */
export const formatCellsData = (
  cell: Omit<
    ComputedCell<{
      x: string
      y: number | null
    }>,
    "opacity" | "borderColor" | "label" | "labelTextColor" | "color"
  >,
  t: (key: string) => string,
): string => {
  if (cell.id === "Actual Positive.Predicted Positive") return `${cell.data.y} ${t("confusionMatrix.truePositive")}`
  if (cell.id === "Actual Negative.Predicted Positive") return `${cell.data.y} ${t("confusionMatrix.falsePositive")}`
  if (cell.id === "Actual Positive.Predicted Negative") return `${cell.data.y} ${t("confusionMatrix.falseNegative")}`
  if (cell.id === "Actual Negative.Predicted Negative") return `${cell.data.y} ${t("confusionMatrix.trueNegative")}`
  return cell.data.y?.toString() || ""
}

export const formatTableData = (data: ThresholdData | undefined, rowName: string): string | number => {
  if (!data || !data[rowName]) return "-"

  return +(+data[rowName]).toFixed(4)
}
